import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../../components/Dashboard/Sidebar/Sidebar';
import Header from '../../components/Dashboard/Header/Header';
import Breadcrumbs from '../../components/Common/Breadcrumbs/Breadcrumbs';
import Pagination from '../../components/Common/Pagination/Pagination';
import api from '../../api/axios';
import toast from 'react-hot-toast';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import {
  Download,
  Plus,
  Search,
  ShoppingCart,
  CheckCircle2,
  Clock,
  Hourglass,
  Eye,
  MoreVertical,
  Calendar,
  Pencil,
  Trash2,
  SlidersHorizontal as Filters,
  X,
  Loader2,
  TrendingUp,
  TrendingDown,
  ChevronDown,
  ChevronUp,
  LayoutDashboard
} from 'lucide-react';
import CustomSelect from '../../components/Common/Select/CustomSelect';
import styles from './MaterialReceipts.module.css';
import Footer from '../../components/Common/Footer/Footer';
import NoData from '../../components/Common/NoData/NoData';
import { useLoading } from '../../context/LoadingContext';

const MySwal = withReactContent(Swal);

const MaterialReceipts = () => {
  const { loading, showLoader, hideLoader } = useLoading();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [isSummaryOpen, setIsSummaryOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    project_id: '',
    vendor_id: '',
    status: ''
  });
  const [filterData, setFilterData] = useState({
    projects: [],
    vendors: []
  });
  const [openMenuIndex, setOpenMenuIndex] = useState(null);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const navigate = useNavigate();

  // API Data State
  const [tableLoading, setTableLoading] = useState(true);
  const [receipts, setReceipts] = useState([]);
  const [paginationData, setPaginationData] = useState({
    total: 0,
    current_page: 1,
    per_page: 10,
    last_page: 1
  });

  // Permission guards
  const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
  const userPermissions = storedUser.permissions || [];
  const isAdmin = (storedUser.role || '').toLowerCase() === 'admin';
  const canCreate = isAdmin || userPermissions.includes('create material receipts');
  const canEdit = isAdmin || userPermissions.includes('edit material receipts');
  const canDelete = isAdmin || userPermissions.includes('delete material receipts');

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    fetchFilterData();
  }, []);

  useEffect(() => {
    fetchReceipts();
  }, [currentPage, itemsPerPage, searchTerm, filters]);

  const fetchFilterData = async () => {
    try {
      const [projectsRes, vendorsRes] = await Promise.all([
        api.get('/projects-dropdown'),
        api.get('/vendors-dropdown')
      ]);
      setFilterData({
        projects: projectsRes.data.data || [],
        vendors: vendorsRes.data.data || []
      });
    } catch (error) {
      console.error('Error fetching filter data:', error);
    }
  };

  const fetchReceipts = async () => {
    setTableLoading(true);
    try {
      const response = await api.get('/material-receipts', {
        skipLoader: true,
        params: {
          page: currentPage,
          per_page: itemsPerPage,
          search: searchTerm,
          project_id: filters.project_id,
          vendor_id: filters.vendor_id,
          status: filters.status
        }
      });
      if (response.data.success) {
        setReceipts(response.data.data.data);
        setPaginationData({
          total: response.data.data.total,
          current_page: response.data.data.current_page,
          per_page: response.data.data.per_page,
          last_page: response.data.data.last_page
        });
      }
    } catch (error) {
      console.error('Error fetching receipts:', error);
      toast.error('Failed to load material receipts');
    } finally {
      setTableLoading(false);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest(`.${styles['action-wrapper']}`)) {
        setOpenMenuIndex(null);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const handleDelete = async (receipt) => {
    setOpenMenuIndex(null);

    MySwal.fire({
      showConfirmButton: true,
      showCancelButton: true,
      confirmButtonText: '<div style="display: flex; align-items: center; justify-content: center; gap: 8px;"><svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 6h18"></path><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"></path><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"></path><line x1="10" y1="11" x2="10" y2="17"></line><line x1="14" y1="11" x2="14" y2="17"></line></svg> Delete Receipt</div>',
      cancelButtonText: 'Cancel',
      buttonsStyling: false,
      customClass: {
        popup: 'custom-swal-popup',
        actions: 'custom-swal-actions',
        confirmButton: 'custom-swal-confirm',
        cancelButton: 'custom-swal-cancel',
      },
      html: `
        <style>
          .custom-swal-popup {
            border-radius: 16px !important;
            padding: 32px 24px 24px 24px !important;
            font-family: 'Outfit', sans-serif !important;
            width: 440px !important;
            box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04) !important;
          }
          @media (max-width: 480px) {
            .custom-swal-popup {
              width: calc(100vw - 32px) !important;
              padding: 24px 16px 20px 16px !important;
            }
            .custom-swal-actions {
              flex-direction: column !important;
              gap: 8px !important;
            }
            .custom-swal-confirm,
            .custom-swal-cancel {
              width: 100% !important;
              justify-content: center !important;
            }
          }
          .custom-swal-actions {
            display: flex !important;
            gap: 12px !important;
            margin-top: 24px !important;
            width: 100% !important;
            padding: 0 !important;
          }
          .custom-swal-confirm {
            background-color: #dc2626 !important;
            color: white !important;
            border: none !important;
            border-radius: 8px !important;
            padding: 10px 24px !important;
            font-size: 14px !important;
            font-weight: 600 !important;
            flex: 1 !important;
            cursor: pointer !important;
            transition: all 0.2s !important;
          }
          .custom-swal-confirm:hover {
            background-color: #b91c1c !important;
          }
          .custom-swal-cancel {
            background-color: #ffffff !important;
            color: #475569 !important;
            border: 1px solid #cbd5e1 !important;
            border-radius: 8px !important;
            padding: 10px 24px !important;
            font-size: 14px !important;
            font-weight: 600 !important;
            flex: 1 !important;
            cursor: pointer !important;
            transition: all 0.2s !important;
          }
          .custom-swal-cancel:hover {
            background-color: #f8fafc !important;
          }
          .custom-icon-wrapper {
            width: 64px;
            height: 64px;
            background-color: #fee2e2;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            margin: 0 auto 20px;
          }
        </style>
        <div class="custom-icon-wrapper">
          <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#dc2626" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
            <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z"></path>
            <line x1="12" y1="9" x2="12" y2="13"></line>
            <line x1="12" y1="17" x2="12.01" y2="17"></line>
          </svg>
        </div>
        <h2 style="font-size: 20px; font-weight: 700; color: #0f172a; margin: 0 0 12px 0;">Delete Material Receipt?</h2>
        <p style="margin: 0 0 24px 0; color: #475569; font-size: 14px; line-height: 1.5;">
          Are you sure you want to delete receipt <strong>${receipt.challan_number || `MR-${receipt.id}`}</strong>?<br/>
          This action cannot be undone and will affect inventory records.
        </p>
      `
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const response = await api.delete(`/material-receipts/${receipt.id}`);
          if (response.data.success) {
            toast.success('Material receipt deleted successfully');
            fetchReceipts();
          }
        } catch (error) {
          console.error('Error deleting receipt:', error);
          toast.error(error.response?.data?.message || 'Failed to delete material receipt');
        }
      }
    });
  };

  const toggleActionMenu = (index, e) => {
    e.stopPropagation();
    if (openMenuIndex === index) {
      setOpenMenuIndex(null);
    } else {
      const rect = e.currentTarget.getBoundingClientRect();
      // On small screens, we might want to offset differently or center the menu
      const menuWidth = 160;
      let leftPos = rect.right - menuWidth;

      // Ensure doesn't go off left edge
      if (leftPos < 10) leftPos = 10;
      // Ensure doesn't go off right edge
      if (leftPos + menuWidth > window.innerWidth - 10) {
        leftPos = window.innerWidth - menuWidth - 10;
      }

      setMenuPosition({
        top: rect.bottom + 4,
        left: leftPos
      });
      setOpenMenuIndex(index);
    }
  };

  const toggleSidebar = () => setIsSidebarOpen(!isSidebarOpen);

  const getStatusClass = (status) => {
    if (!status) return styles['pill-pending'];
    const s = status.toLowerCase();
    if (s === 'completed') return styles['pill-completed'];
    if (s === 'partial') return styles['pill-partial'];
    return styles['pill-open'];
  };

  return (
    <div className={`${styles['material-receipts-container']} ${isSidebarOpen ? styles['sidebar-open'] : ''}`}>
      {isSidebarOpen && <div className={styles['sidebar-overlay']} onClick={() => setIsSidebarOpen(false)}></div>}
      <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />

      <div className={styles['content-wrapper']}>
        <Header onMenuClick={toggleSidebar} />

        <main className={styles['main-content']}>
          <Breadcrumbs />

          <div className={styles['page-header']}>
            <div className={styles['header-title-section']}>
              <h1>Material Receipts</h1>
              <p>Manage and track all inbound material shipments and PO completions.</p>
            </div>
            <div className={styles['header-actions']}>
              <button className={styles['control-btn']}>
                <Download size={18} />
                <span>Export</span>
              </button>
              {canCreate && (
                <button className={styles['add-btn']} onClick={() => navigate('/receipts/add')}>
                  <Plus size={20} />
                  <span>Add Receipt</span>
                </button>
              )}
            </div>
          </div>

          {/* Metrics — collapsible on mobile */}
          <div className={styles['summary-collapsible']}>
            <button
              className={styles['summary-toggle']}
              onClick={() => setIsSummaryOpen(prev => !prev)}
            >
              <div className={styles['summary-toggle-left']}>
                <LayoutDashboard size={20} style={{ color: '#f97316' }} />
                <div className={styles['summary-toggle-text']}>
                  <span className={styles['summary-toggle-title']}>Summary</span>
                  <span className={styles['summary-toggle-hint']}>Tap to {isSummaryOpen ? 'collapse' : 'expand'} metrics</span>
                </div>
              </div>
              {isSummaryOpen ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
            </button>

            <div className={`${styles['metrics-grid']} ${isSummaryOpen ? styles['summary-expanded'] : styles['summary-collapsed']}`}>
              <div className={`${styles['metric-card']} ${styles['primary']}`}>
                <div className={styles['metric-info']}>
                  <span className={styles['label']}>Total Receipts</span>
                  <span className={styles['value']}>{paginationData.total}</span>
                </div>
                <div className={`${styles['card-subtext']} ${styles['positive']}`}>
                  <TrendingUp size={14} /> Tracking all shipments
                </div>
              </div>

              <div className={`${styles['metric-card']} ${styles['success']}`}>
                <div className={styles['metric-info']}>
                  <span className={styles['label']}>Completed Receipts</span>
                  <span className={styles['value']}>{receipts.filter(r => r.status === 'completed').length}</span>
                </div>
                <div className={`${styles['card-subtext']} ${styles['neutral']}`}>
                  Fully fulfilled POs
                </div>
              </div>

              <div className={`${styles['metric-card']} ${styles['warning']}`}>
                <div className={styles['metric-info']}>
                  <span className={styles['label']}>Partial Shipments</span>
                  <span className={styles['value']}>{receipts.filter(r => r.status === 'partial').length}</span>
                </div>
                <div className={`${styles['card-subtext']} ${styles['warning']}`}>
                  Requires follow-up
                </div>
              </div>

              <div className={`${styles['metric-card']} ${styles['danger']}`}>
                <div className={styles['metric-info']}>
                  <span className={styles['label']}>Recent Activity</span>
                  <span className={styles['value']}>Active</span>
                </div>
                <div className={`${styles['card-subtext']} ${styles['warning']}`}>
                  Synchronized with backend
                </div>
              </div>
            </div>
          </div>

          <div className={styles['table-controls']}>
            <div className={styles['search-wrapper']}>
              <Search className={styles['search-icon']} size={18} />
              <input
                type="text"
                placeholder="Search Challan #, PO #, Project or Vendor..."
                className={styles['search-input']}
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
              />
            </div>
            <div className={styles['filter-actions']}>
              <div className={styles['per-page-select-wrapper']}>
                <CustomSelect
                  isSearchable={false}
                  backgroundColor="white"
                  value={{ value: itemsPerPage, label: `${itemsPerPage} per page` }}
                  onChange={(option) => {
                    setItemsPerPage(option.value);
                    setCurrentPage(1);
                  }}
                  options={[
                    { value: 10, label: '10 per page' },
                    { value: 25, label: '25 per page' },
                    { value: 50, label: '50 per page' },
                    { value: 100, label: '100 per page' }
                  ]}
                />
              </div>
              <button
                className={`${styles['filter-btn']} ${showFilters ? styles['active'] : ''}`}
                onClick={() => setShowFilters(!showFilters)}
              >
                <Filters size={18} />
                <span>Filters</span>
                {(filters.project_id || filters.vendor_id || filters.status) && (
                  <span className={styles['filter-badge']}></span>
                )}
              </button>
            </div>
          </div>

          {showFilters && (
            <div className={styles['filters-panel']}>
              <div className={styles['filters-header']}>
                <div className={styles['filters-title-group']}>
                  <h3>Filter Receipts</h3>
                  <p>Refine your material receipts list by project, vendor or status.</p>
                </div>
                <button
                  className={styles['close-icon-btn']}
                  onClick={() => setShowFilters(false)}
                >
                  <X size={18} />
                </button>
              </div>

              <div className={styles['filters-grid']}>
                <div className={styles['filter-group']}>
                  <label>By Project</label>
                  <CustomSelect
                    value={filters.project_id ? {
                      value: filters.project_id,
                      label: filterData.projects.find(p => p.id === filters.project_id)?.project_name
                    } : null}
                    onChange={(opt) => {
                      setFilters(prev => ({ ...prev, project_id: opt ? opt.value : '' }));
                      setCurrentPage(1);
                    }}
                    options={filterData.projects.map(p => ({ value: p.id, label: p.project_name }))}
                    placeholder="All Projects"
                    isClearable={true}
                  />
                </div>

                <div className={styles['filter-group']}>
                  <label>By Vendor</label>
                  <CustomSelect
                    value={filters.vendor_id ? {
                      value: filters.vendor_id,
                      label: filterData.vendors.find(v => v.id === filters.vendor_id)?.vendor_name
                    } : null}
                    onChange={(opt) => {
                      setFilters(prev => ({ ...prev, vendor_id: opt ? opt.value : '' }));
                      setCurrentPage(1);
                    }}
                    options={filterData.vendors.map(v => ({ value: v.id, label: v.vendor_name }))}
                    placeholder="All Vendors"
                    isClearable={true}
                  />
                </div>

                <div className={styles['filter-group']}>
                  <label>By Status</label>
                  <CustomSelect
                    value={filters.status ? {
                      value: filters.status,
                      label: filters.status.charAt(0).toUpperCase() + filters.status.slice(1)
                    } : null}
                    onChange={(opt) => {
                      setFilters(prev => ({ ...prev, status: opt ? opt.value : '' }));
                      setCurrentPage(1);
                    }}
                    options={[
                      { value: 'partial', label: 'Partial' },
                      { value: 'completed', label: 'Completed' }
                    ]}
                    placeholder="All Statuses"
                    isClearable={true}
                  />
                </div>
              </div>

              <div className={styles['filters-footer']}>
                <button
                  className={styles['reset-btn']}
                  onClick={() => {
                    setFilters({ project_id: '', vendor_id: '', status: '' });
                    setCurrentPage(1);
                  }}
                >
                  Reset
                </button>
                <button
                  className={styles['filters-close-btn']}
                  onClick={() => setShowFilters(false)}
                >
                  Apply Filters
                </button>
              </div>
            </div>
          )}

          <div className={styles['data-view-container']}>
            {/* Desktop View */}
            <div className={styles['table-wrapper']}>
              <table className={styles['receipt-table']}>
                <thead>
                  <tr>
                    <th>RECEIPT / CHALLAN</th>
                    <th>PROJECT</th>
                    <th>PO NUMBER</th>
                    <th>VENDOR</th>
                    <th>ITEMS</th>
                    <th>QTY RECEIVED</th>
                    <th>RECEIVED BY</th>
                    <th>STATUS</th>
                    <th>DATE</th>
                    <th className={styles['actions-cell']}>ACTIONS</th>
                  </tr>
                </thead>
                <tbody>
                  {tableLoading ? (
                    <tr>
                      <td colSpan="10" style={{ textAlign: 'center', padding: '40px' }}>
                        <Loader2 size={32} className={styles['spinner']} style={{ color: '#f59e0b', margin: '0 auto' }} />
                        <p style={{ color: '#64748b', marginTop: '10px' }}>Loading material receipts...</p>
                      </td>
                    </tr>
                  ) : receipts.length > 0 ? (
                    receipts.map((row, index) => (
                      <tr key={row.id}>
                        <td className={styles['id-cell']}>
                          <div className={styles['receipt-info']}>
                            <span className={styles['receipt-text']}>{row.challan_number || `MR-${row.id}`}</span>
                          </div>
                        </td>
                        <td className={styles['project-cell']}>{row.purchase_order?.project?.project_name || '---'}</td>
                        <td className={styles['po-cell']}>{row.purchase_order?.po_number || '---'}</td>
                        <td className={styles['vendor-name']}>{row.purchase_order?.vendor?.vendor_name || '---'}</td>
                        <td>{row.items_count || 0} Items</td>
                        <td style={{ fontWeight: 700, color: '#1e293b' }}>{row.total_quantity_received}</td>
                        <td style={{ fontSize: '0.85rem' }}>{row.received_by?.name || '---'}</td>
                        <td>
                          <span className={`${styles['status-pill']} ${getStatusClass(row.status)}`}>
                            {row.status?.toUpperCase() || 'OPEN'}
                          </span>
                        </td>
                        <td>{new Date(row.received_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</td>
                        <td className={styles['actions-cell']}>
                          <div className={styles['action-wrapper']}>
                            <button
                              className={`${styles['action-btn']} ${openMenuIndex === index ? styles['active'] : ''}`}
                              onClick={(e) => toggleActionMenu(index, e)}
                            >
                              <MoreVertical size={18} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <NoData message="No material receipts found." colSpan={10} isTable={true} />
                  )}
                </tbody>
              </table>
            </div>

            {/* Mobile View */}
            <div className={styles['receipt-cards']}>
              {tableLoading ? (
                <div style={{ textAlign: 'center', padding: '40px' }}>
                  <Loader2 size={32} className={styles['spinner']} style={{ color: '#f59e0b', margin: '0 auto' }} />
                  <p style={{ color: '#64748b', marginTop: '10px' }}>Loading material receipts...</p>
                </div>
              ) : receipts.length > 0 ? (
                receipts.map((row, index) => (
                  <div key={row.id} className={styles['receipt-card']}>
                    <div className={styles['card-main']}>
                      <div className={styles['card-info']}>
                        <span className={styles['card-id']}>{row.challan_number || `MR-${row.id}`}</span>
                        <span className={styles['card-po']}>{row.purchase_order?.po_number || '---'}</span>
                      </div>
                      <div className={styles['action-wrapper']}>
                        <button
                          className={`${styles['action-btn']} ${openMenuIndex === index ? styles['active'] : ''}`}
                          onClick={(e) => toggleActionMenu(index, e)}
                        >
                          <MoreVertical size={18} />
                        </button>
                      </div>
                    </div>
                    <div className={styles['card-details']}>
                      <div className={styles['detail-item']} style={{ gridColumn: 'span 2' }}>
                        <span className={styles['detail-label']}>Project</span>
                        <span className={styles['detail-value']}>{row.purchase_order?.project?.project_name || '---'}</span>
                      </div>
                      <div className={styles['detail-item']} style={{ gridColumn: 'span 2' }}>
                        <span className={styles['detail-label']}>Vendor</span>
                        <span className={styles['detail-value']}>{row.purchase_order?.vendor?.vendor_name || '---'}</span>
                      </div>
                      <div className={styles['detail-item']}>
                        <span className={styles['detail-label']}>Received Qty</span>
                        <span className={styles['detail-value']} style={{ fontWeight: 800 }}>{row.total_quantity_received}</span>
                      </div>
                      <div className={styles['detail-item']}>
                        <span className={styles['detail-label']}>By</span>
                        <span className={styles['detail-value']}>{row.received_by?.name || '---'}</span>
                      </div>
                    </div>
                    <div className={styles['card-footer']}>
                      <div className={styles['footer-left']}>
                        <Calendar size={14} className={styles['text-muted']} />
                        <span>{new Date(row.received_date).toLocaleDateString('en-IN')}</span>
                      </div>
                      <span className={`${styles['status-pill']} ${getStatusClass(row.status)}`}>
                        {row.status?.toUpperCase() || 'OPEN'}
                      </span>
                    </div>
                  </div>
                ))
              ) : (
                <NoData message="No material receipts found." isTable={false} />
              )}
            </div>

            <div className={styles['desktop-pagination']}>
              <Pagination
                currentPage={paginationData.current_page}
                totalItems={paginationData.total}
                itemsPerPage={paginationData.per_page}
                onPageChange={(page) => setCurrentPage(page)}
              />
            </div>
          </div>

          <div className={styles['mobile-pagination']}>
            <Pagination
              currentPage={paginationData.current_page}
              totalItems={paginationData.total}
              itemsPerPage={paginationData.per_page}
              onPageChange={(page) => setCurrentPage(page)}
            />
          </div>

          {/* Global Action Dropdown */}
          {openMenuIndex !== null && (
            <div
              className={[styles['action-dropdown'], styles['shadow-premium']].join(' ')}
              style={{
                position: 'fixed',
                top: `${menuPosition.top}px`,
                left: `${menuPosition.left}px`,
                zIndex: 9999
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <button
                className={styles['dropdown-item']}
                onClick={() => {
                  setOpenMenuIndex(null);
                  navigate(`/receipts/view/${receipts[openMenuIndex]?.id}`);
                }}
              >
                <Eye size={16} /> View Receipt
              </button>
              {canEdit && (
                <button
                  className={styles['dropdown-item']}
                  onClick={() => {
                    setOpenMenuIndex(null);
                    navigate(`/receipts/edit/${receipts[openMenuIndex]?.id}`);
                  }}
                >
                  <Pencil size={16} /> Edit Details
                </button>
              )}
              {canDelete && (
                <>
                  <div className={styles['dropdown-divider']}></div>
                  <button
                    className={[styles['dropdown-item'], styles['deselect']].join(' ')}
                    onClick={() => handleDelete(receipts[openMenuIndex])}
                  >
                    <Trash2 size={16} /> Delete Record
                  </button>
                </>
              )}
            </div>
          )}

          <Footer />
        </main>
      </div>
    </div>
  );
};

export default MaterialReceipts;
